import React, { createContext, useContext, useState, useCallback } from 'react';
import { ChatRoom } from '../services/WebSocketService';
import { useWebSocketChat } from './WebSocketChatContext';
import { useUser } from './UserContext';
import webSocketService from '../services/WebSocketService';

// Floating chat window interface
interface FloatingChat {
   id: string;
   channel: ChatRoom;
   minimized: boolean;
   position: { x: number; y: number };
}

// Contact interface
interface Contact {
   id: string;
   userId: string;
   displayName: string;
   email: string;
   photoURL?: string;
   source?: string;
}

interface FloatingChatContextType {
   floatingChats: FloatingChat[];
   floatingChatListOpen: boolean;
   floatingChatListMinimized: boolean;
   showMainChat: boolean;
   selectedContact: Contact | null;
   firstMessageModalOpen: boolean;
   firstMessage: string;
   sendingFirstMessage: boolean;
   existingChatMessage: string | null;
   showExistingChatMessage: boolean;

   // Actions
   openFloatingChat: (channel: ChatRoom) => Promise<void>;
   closeFloatingChat: (chatId: string) => void;
   toggleFloatingChat: (chatId: string) => void;
   closeAllFloatingChats: () => void;
   openFloatingChatForContact: (contact: Contact) => Promise<void>;

   // Chat list actions
   setFloatingChatListOpen: (open: boolean) => void;
   setFloatingChatListMinimized: (minimized: boolean) => void;
   setShowMainChat: (show: boolean) => void;

   // First message modal actions
   setSelectedContact: (contact: Contact | null) => void;
   setFirstMessageModalOpen: (open: boolean) => void;
   setFirstMessage: (message: string) => void;
   handleSendFirstMessage: () => Promise<void>;
   handleCloseFirstMessageModal: () => void;
   setExistingChatMessage: (message: string | null) => void;
   setShowExistingChatMessage: (show: boolean) => void;
}

const FloatingChatContext = createContext<FloatingChatContextType | undefined>(undefined);

export const useFloatingChat = () => {
   const context = useContext(FloatingChatContext);
   if (!context) {
      throw new Error('useFloatingChat must be used within a FloatingChatProvider');
   }
   return context;
};

export const FloatingChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
   const [floatingChats, setFloatingChats] = useState<FloatingChat[]>([]);
   const [floatingChatListOpen, setFloatingChatListOpen] = useState(false);
   const [floatingChatListMinimized, setFloatingChatListMinimized] = useState(false);
   const [showMainChat, setShowMainChat] = useState(false);
   const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
   const [firstMessageModalOpen, setFirstMessageModalOpen] = useState(false);
   const [firstMessage, setFirstMessage] = useState('');
   const [sendingFirstMessage, setSendingFirstMessage] = useState(false);
   const [existingChatMessage, setExistingChatMessage] = useState<string | null>(null);
   const [showExistingChatMessage, setShowExistingChatMessage] = useState(false);

   const { loadRoomMessages, refreshChannels, messages, channels } = useWebSocketChat();
   const { currentUser } = useUser();

   // Function to open a floating chat
   const openFloatingChat = useCallback(
      async (channel: ChatRoom) => {
         // Check if chat is already open
         const existingChat = floatingChats.find((fc) => fc.channel.id === channel.id);
         if (existingChat) {
            // Just focus/unminimize if already open
            setFloatingChats((prev) =>
               prev.map((fc) => (fc.id === existingChat.id ? { ...fc, minimized: false } : fc))
            );
            return;
         }

         // Create new floating chat
         const newFloatingChat: FloatingChat = {
            id: `floating-${channel.id}-${Date.now()}`,
            channel,
            minimized: false,
            position: { x: 0, y: 0 },
         };

         setFloatingChats((prev) => [...prev, newFloatingChat]);

         // Load messages for the channel
         if (!messages[channel.id]) {
            loadRoomMessages(channel.id);
         }
      },
      [floatingChats, loadRoomMessages, messages]
   );

   // Function to open floating chat for a contact
   const openFloatingChatForContact = useCallback(
      async (contact: Contact) => {
         if (!currentUser) return;

         // Create room ID in the same format backend expects
         const currentUserId = currentUser.uid;
         const roomId =
            currentUserId < contact.userId
               ? `chat_${currentUserId}_${contact.userId}`
               : `chat_${contact.userId}_${currentUserId}`;

         // Check if a one-on-one chat already exists with this contact
         const existingChannel = channels.find((channel) => channel.id === roomId);

         if (existingChannel) {
            // Open the existing floating chat
            await openFloatingChat(existingChannel);
            return;
         }

         // If no existing chat, show the first message modal
         setSelectedContact(contact);
         setFirstMessageModalOpen(true);
      },
      [currentUser, channels, openFloatingChat, setSelectedContact, setFirstMessageModalOpen]
   );

   // Function to close a floating chat
   const closeFloatingChat = useCallback((chatId: string) => {
      setFloatingChats((prev) => prev.filter((fc) => fc.id !== chatId));
   }, []);

   // Function to minimize/maximize a floating chat
   const toggleFloatingChat = useCallback((chatId: string) => {
      setFloatingChats((prev) =>
         prev.map((fc) => (fc.id === chatId ? { ...fc, minimized: !fc.minimized } : fc))
      );
   }, []);

   // Function to close all floating chats
   const closeAllFloatingChats = useCallback(() => {
      setFloatingChats([]);
   }, []);

   // Function to handle sending first message
   const handleSendFirstMessage = useCallback(async () => {
      try {
         if (!currentUser || !selectedContact) {
            throw new Error('User not authenticated or no contact selected');
         }

         if (!firstMessage.trim()) {
            throw new Error('Message cannot be empty');
         }

         setSendingFirstMessage(true);

         const otherUserId = selectedContact.userId;
         const currentUserId = currentUser.uid;
         const roomId =
            currentUserId < otherUserId
               ? `chat_${currentUserId}_${otherUserId}`
               : `chat_${otherUserId}_${currentUserId}`;

         console.log('🚀 Starting first message send process...');
         console.log('📋 Details:', {
            currentUserId,
            otherUserId,
            roomId,
            selectedContact: selectedContact.displayName,
            messageLength: firstMessage.trim().length,
            webSocketConnected: webSocketService.isConnected,
         });

         // Check WebSocket connection status
         if (!webSocketService.isConnected) {
            console.log('🔄 WebSocket not connected, attempting to connect...');
            try {
               await webSocketService.connect(
                  currentUserId,
                  currentUser.displayName || currentUser.email?.split('@')[0] || 'User'
               );
               console.log('✅ WebSocket connected successfully');
            } catch (connectionError: any) {
               console.error('❌ WebSocket connection failed:', connectionError);
               throw new Error(
                  'Unable to establish connection. Please check your internet connection and try again.'
               );
            }
         }

         // Step 1: Create or get existing room
         console.log('🏠 Creating/getting chat room...');
         console.log('🔍 Room creation details:', {
            currentUserId,
            otherUserId,
            expectedRoomId: roomId,
            webSocketConnected: webSocketService.isConnected,
            currentUserSet: !!webSocketService.getCurrentUser(),
         });

         let chatRoom: any;
         try {
            chatRoom = await webSocketService.createOneOnOneChat(otherUserId);
            console.log('✅ Chat room ready:', {
               id: chatRoom.id,
               name: chatRoom.name,
               members: chatRoom.members,
            });
         } catch (roomError: any) {
            console.error('❌ Failed to create chat room via WebSocket:', roomError);
            console.error('❌ Room error details:', {
               message: roomError.message,
               stack: roomError.stack,
               name: roomError.name,
               cause: roomError.cause,
            });

            // Try fallback approach: create chat room directly in Firestore
            console.log('🔄 Attempting fallback chat room creation...');
            try {
               const { doc, setDoc, serverTimestamp } = await import('firebase/firestore');
               const { db } = await import('../firebase/config');

               const chatRoomData = {
                  roomId,
                  roomType: 'direct',
                  name: selectedContact.displayName,
                  description: 'Direct message',
                  createdAt: serverTimestamp(),
                  createdBy: currentUserId,
                  members: [currentUserId, otherUserId],
                  isPrivate: true,
                  lastActivity: serverTimestamp(),
               };

               await setDoc(doc(db, 'chatRooms', roomId), chatRoomData);
               console.log('✅ Fallback chat room created successfully');

               // Create a mock ChatRoom object for consistency
               chatRoom = {
                  id: roomId,
                  name: selectedContact.displayName,
                  description: 'Direct message',
                  createdBy: currentUserId,
                  createdAt: new Date(),
                  members: [currentUserId, otherUserId],
                  isPrivate: true,
               };
            } catch (fallbackError: any) {
               console.error('❌ Fallback chat room creation also failed:', fallbackError);

               // Provide more specific error messages based on the error type
               let errorMessage = 'Failed to create chat room. Please try again.';

               if (roomError.message) {
                  if (roomError.message.includes('not authenticated')) {
                     errorMessage = 'Authentication error. Please refresh the page and try again.';
                  } else if (roomError.message.includes('timeout')) {
                     errorMessage =
                        'Connection timeout. Please check your internet connection and try again.';
                  } else if (roomError.message.includes('permission')) {
                     errorMessage =
                        'Permission denied. You may not have access to chat with this user.';
                  } else if (roomError.message.includes('not found')) {
                     errorMessage =
                        'User not found. Please make sure the user exists and try again.';
                  } else {
                     errorMessage = `Chat room creation failed: ${roomError.message}`;
                  }
               }

               throw new Error(errorMessage);
            }
         }

         // Step 2: Send the message
         console.log('📤 Sending message...');
         try {
         await webSocketService.sendMessage(roomId, firstMessage.trim());
            console.log('✅ Message sent successfully via WebSocket');
         } catch (messageError: any) {
            console.error('❌ Failed to send message via WebSocket:', messageError);

            // Try fallback approach: save message directly to Firestore
            console.log('🔄 Attempting fallback message sending...');
            try {
               const { collection, addDoc, serverTimestamp } = await import('firebase/firestore');
               const { db } = await import('../firebase/config');

               const messageData = {
                  roomId,
                  senderId: currentUserId,
                  senderName: currentUser.displayName || currentUser.email?.split('@')[0] || 'User',
                  text: firstMessage.trim(),
                  timestamp: serverTimestamp(),
                  messageType: 'text',
               };

               await addDoc(collection(db, 'messages'), messageData);
               console.log('✅ Fallback message sent successfully to Firestore');
            } catch (fallbackMessageError: any) {
               console.error('❌ Fallback message sending also failed:', fallbackMessageError);
               throw new Error(
                  `Failed to send message: ${messageError.message || 'Unknown error'}`
               );
            }
         }

         // Step 3: Notify the other user (non-blocking)
         try {
            webSocketService.notifyUserNewChat(
               otherUserId,
               roomId,
               currentUser.displayName || 'Someone',
               firstMessage.trim()
            );
            console.log('📢 Notification sent');
         } catch (notifyError: any) {
            console.warn(
               '⚠️ Failed to send notification (message was sent successfully):',
               notifyError
            );
         }

         // Step 4: Refresh channels and prepare to open chat
         console.log('🔄 Refreshing channels...');
         try {
            await refreshChannels();
            console.log('✅ Channels refreshed');
         } catch (refreshError: any) {
            console.warn('⚠️ Failed to refresh channels:', refreshError);
         }

         // Step 5: Clean up modal and show success
         setFirstMessage('');
         setSelectedContact(null);
         setFirstMessageModalOpen(false);

         // Show success message
         setExistingChatMessage(`Message sent to ${selectedContact.displayName}! Opening chat...`);
         setShowExistingChatMessage(true);

         // Step 6: Open the chat after a brief delay
         setTimeout(async () => {
            console.log('🔍 Looking for chat room to open...');

            // Try to find the channel in current channels first
            let targetChannel = channels.find((channel) => channel.id === roomId);

            if (!targetChannel) {
               console.log('📡 Channel not found, refreshing channels again...');
               try {
                  await refreshChannels();
                  // Wait a bit for state to update
                  setTimeout(() => {
                     const refreshedChannel = channels.find((channel) => channel.id === roomId);
                     if (refreshedChannel) {
                        console.log('✅ Found channel after refresh, opening chat');
                        openFloatingChat(refreshedChannel);
                     } else {
                        console.log(
                           'ℹ️ Channel not found in floating chat, but message was sent successfully'
                        );
                        // Update success message to indicate chat is available in main chat section
                        setExistingChatMessage(
                           `Message sent successfully! You can find your conversation with ${selectedContact.displayName} in the Chat section.`
                        );
                        setShowExistingChatMessage(true);
                     }
                  }, 1000);
               } catch (finalRefreshError: any) {
                  console.warn('⚠️ Final refresh failed:', finalRefreshError);
                  setExistingChatMessage(
                     `Message sent successfully! You can find your conversation with ${selectedContact.displayName} in the Chat section.`
                  );
                  setShowExistingChatMessage(true);
               }
            } else {
               console.log('✅ Found channel, opening floating chat');
               await openFloatingChat(targetChannel);
            }
         }, 1500);
      } catch (error: any) {
         console.error('❌ Error in handleSendFirstMessage:', error);

         // Show user-friendly error message
         let errorMessage = 'Failed to send message. Please try again.';

         if (error.message) {
            if (error.message.includes('not authenticated')) {
               errorMessage = 'Session expired. Please refresh the page and try again.';
            } else if (error.message.includes('connection')) {
               errorMessage = 'Connection problem. Please check your internet and try again.';
            } else if (error.message.includes('timeout')) {
               errorMessage = 'Request timed out. Please try again.';
            } else {
               errorMessage = error.message;
            }
         }

         setExistingChatMessage(`Error: ${errorMessage}`);
         setShowExistingChatMessage(true);
      } finally {
         setSendingFirstMessage(false);
      }
   }, [
      currentUser,
      selectedContact,
      firstMessage,
      setFirstMessage,
      setSelectedContact,
      setFirstMessageModalOpen,
      setSendingFirstMessage,
      refreshChannels,
      channels,
      openFloatingChat,
      setExistingChatMessage,
      setShowExistingChatMessage,
   ]);

   // Function to close first message modal
   const handleCloseFirstMessageModal = useCallback(() => {
      setFirstMessageModalOpen(false);
      setSelectedContact(null);
      setFirstMessage('');
   }, []);

   const value: FloatingChatContextType = {
      floatingChats,
      floatingChatListOpen,
      floatingChatListMinimized,
      showMainChat,
      selectedContact,
      firstMessageModalOpen,
      firstMessage,
      sendingFirstMessage,
      existingChatMessage,
      showExistingChatMessage,

      openFloatingChat,
      closeFloatingChat,
      toggleFloatingChat,
      closeAllFloatingChats,
      openFloatingChatForContact,

      setFloatingChatListOpen,
      setFloatingChatListMinimized,
      setShowMainChat,

      setSelectedContact,
      setFirstMessageModalOpen,
      setFirstMessage,
      handleSendFirstMessage,
      handleCloseFirstMessageModal,
      setExistingChatMessage,
      setShowExistingChatMessage,
   };

   return <FloatingChatContext.Provider value={value}>{children}</FloatingChatContext.Provider>;
};
